// src/store/modules/statistiques/store.js

import AuthService from "@services/auth.service";
import ApiService from "@services/api.service";
import { Notify } from 'quasar';

// Helper to normalize students before storing
function normalizeEtudiants(etudiants) {
  if (!Array.isArray(etudiants)) return []
  return etudiants.map(e => {
    const copy = { ...e }
    if ((copy.prescriptor === undefined || copy.prescriptor === null || copy.prescriptor === '') && copy.other_prescriptor) {
      copy.prescriptor = 9
    }
    return copy
  })
}

export const statistiques_store = {
  namespaced: true,

  state: {
    etudiants: [],
    periode: new Date().getFullYear(),
    filters: {},
    optionsAntennes: [],
    optionsDiplomes: [],
    optionsSchoolLevel: [],
    optionsPeriodSelectText: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i), // les 5 dernieres années
    optionsSex: [
      { value: -1, name: 'Tous' },
      { value: 0, name: 'Homme' },
      { value: 1, name: 'Femme' }
    ],
    optionsEtat: [
      { value: -1, name: 'Tous' },
      { value: 0, name: 'Présent' },
      { value: 1, name: 'Sortie' }
    ],
    loading: false
  },

  mutations: {
    setOptionsAntennes(state, value) {
      state.optionsAntennes = value
    },
    setOptionsDiplomes(state, value) {
      state.optionsDiplomes = value
    },
    setOptionsSchoolLevel(state, value) {
      state.optionsSchoolLevel = value
    },
    setOptionsPeriodSelectText(state, value) {
      state.optionsPeriodSelectText = value
    },
    setFilters(state, value) {
      state.filters = value
    },
    setEtudiants(state, etudiants) {
      state.etudiants = etudiants;
    },
    setPeriode(state, periode) {
      state.periode = periode;
    },
    setLoading(state, loading) {
      state.loading = loading;
    }
  },

  actions: {
    async loadInitialEtudiants(context) {
      context.commit('setLoading', true);
      try {
        const filters = context.state.filters || {}
        const query = {}

        if (filters.periodSelectText) {
          query.annee = filters.periodSelectText
        }

        if (filters.antenne?.['@id'] && filters.antenne['@id'] !== 0) {
          query.antenne = filters.antenne['@id'].split('/').pop()
        }

        if (filters.etat?.value !== undefined && filters.etat?.value !== -1) {
          query.etat = filters.etat.value
        } else {
          query.etat = 0
        }

        if (filters.sex?.value !== undefined && filters.sex?.value !== -1) {
          query.civility = filters.sex.value
        }

        if (filters.diplome?.['@id'] && filters.diplome['@id'] !== 0) {
          query.diplome = filters.diplome['@id'].split('/').pop()
        }

        if (filters.schoolLevel && filters.schoolLevel !== 'Tous') {
          query.lastGradeLevel = filters.schoolLevel
        }

        const queryString = new URLSearchParams(query).toString()
        const endpoint = queryString ? `etudiants_statistiques_filtres?${queryString}` : 'etudiants_statistiques'
        const { data } = await ApiService.getRequest(endpoint)
        const members = data && data['hydra:member'] ? data['hydra:member'] : (Array.isArray(data) ? data : [])
        context.commit('setEtudiants', normalizeEtudiants(members));
      } catch (error) {
        console.error("Erreur chargement initial :", error);
        Notify.create({
          message: "Erreur lors du chargement initial des statistiques.",
          color: "red"
        });
      } finally {
        context.commit('setLoading', false);
      }
    },

    async applyFilters(context, filters) {
      context.commit('setLoading', true)
      context.commit('setFilters', filters)

      // Mémoriser l'année sélectionnée
      if (filters.periodSelectText != null) {
        context.commit('setPeriode', filters.periodSelectText)
      }

      const query = {}

      if (filters.periodSelectText) {
        query.annee = filters.periodSelectText
      }

      if (filters.antenne?.['@id'] && filters.antenne['@id'] !== 0) {
        query.antenne = filters.antenne['@id'].split('/').pop()
      }

      if (filters.etat?.value !== -1) {
        query.etat = filters.etat.value
      }

      if (filters.sex?.value !== -1) {
        query.civility = filters.sex.value
      }

      if (filters.diplome?.['@id'] && filters.diplome['@id'] !== 0) {
        query.diplome = filters.diplome['@id'].split('/').pop()
      }

      if (filters.schoolLevel && filters.schoolLevel !== 'Tous') {
        query.lastGradeLevel = filters.schoolLevel
      }

      const queryString = new URLSearchParams(query).toString()

      try {
        const { data } = await ApiService.getRequest(`etudiants_statistiques_filtres?${queryString}`)
        const members = data && data['hydra:member'] ? data['hydra:member'] : (Array.isArray(data) ? data : [])
        context.commit('setEtudiants', normalizeEtudiants(members))
      } catch (error) {
        console.error("Erreur filtre stats :", error)
        Notify.create({
          message: "Erreur lors du filtrage des statistiques.",
          color: "red"
        })
      } finally {
        context.commit('setLoading', false)
      }
    },


    async initFilterOptions({ commit, state, dispatch, rootState, rootGetters }) {
      const user = rootState.auth.user
      const userCfa = user.antenne.cfa
      const userAntenne = user.antenne

      // 5 années glissantes à partir de l'année courante
      const currentYear = new Date().getFullYear()
      const rollingYears = Array.from({ length: 5 }, (_, i) => currentYear - i)
      commit('setOptionsPeriodSelectText', rollingYears)

      // Niveaux scolaires
      const niveaux = ['Tous'].concat(rootGetters.getNiveauDeFormation || [])
      commit('setOptionsSchoolLevel', niveaux)

      if (AuthService.checkRole('ROLE_SUPERVISOR')) {
        const res = await ApiService.getRequest('antennes')
        const antennes = [{ '@id': 0, name: 'Toutes' }, ...res.data['hydra:member'].sort((a, b) => a.name.localeCompare(b.name))]
        commit('setOptionsAntennes', antennes)

        commit('setFilters', {
          antenne: antennes[0],
          periodSelectText: currentYear,
          sex: state.optionsSex[0],
          schoolLevel: niveaux[0],
          etat: state.optionsEtat[0],
          diplome: null
        })
      } else {
        const res = await ApiService.getRequest(`antennes_stats_detail/${userAntenne.id}`)
        commit('setFilters', {
          antenne: res.data,
          periodSelectText: currentYear,
          sex: state.optionsSex[0],
          schoolLevel: niveaux[0],
          etat: state.optionsEtat[0],
          diplome: null
        })
      }

      dispatch('updateDiplomes')
    },

    updateDiplomes({ state, commit, rootState }) {
      const antenne = state.filters.antenne
      const user = rootState.auth.user
      let diplomes = []

      if (antenne && antenne['@id'] !== 0) {
        diplomes = antenne.diplomes
      } else {
        diplomes = AuthService.checkRole('ROLE_SUPERVISOR')
          ? user.antenne.cfa.diplomes
          : user.antenne.diplomes
      }

      const options = [{ '@id': 0, complement_name: 'Tous' }, ...diplomes]
      commit('setOptionsDiplomes', options)
      commit('setFilters', {
        ...state.filters,
        diplome: options[0]
      })
    }

  },

  getters: {
    getEtudiants(state) {
      return state.etudiants;
    },
    getPeriode(state) {
      return state.periode;
    },
    getFilters(state) {
      return state.filters;
    },
    isLoading(state) {
      return state.loading;
    },
    getOptionsAntennes(state) {
      return state.optionsAntennes;
    },
    getOptionsEtat(state) {
      return state.optionsEtat;
    },
    getOptionsSex(state) {
      return state.optionsSex;
    },
    getOptionsPeriodSelectText(state) {
      return state.optionsPeriodSelectText;
    },
    getOptionsDiplomes(state) {
      return state.optionsDiplomes;
    },
    getOptionsSchoolLevel(state) {
      return state.optionsSchoolLevel;
    }
  }
};

